import React, { useEffect, useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { resizeImage } from "../../components/utils/resizeImage";
import { cropImageFile } from "../../components/utils/cropImage";
import { X, ExternalLink } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import SocialPlatformEmbed from "./socialPlatformEmbed";
import { uploadImageWithPath, uploadVideo } from "../../services/firebase";
import { addLeagueFeed, updateLeagueProgressStep } from "./firestoreQuery";
import { IconImg, IconUser, IconVideo } from "../CustomIcons";
import { update } from "lodash";
// Helper functions for social URL detection
function getPlatform(
  url: string
): "youtube" | "x" | "twitter" | "instagram" | "tiktok" | "facebook" | null {
  if (/youtu(\.be|be\.com)/.test(url)) return "youtube";
  if (/x\.com|twitter\.com/.test(url)) return "x";
  if (/instagram\.com/.test(url)) return "instagram";
  if (/tiktok\.com/.test(url)) return "tiktok";
  if (/facebook\.com|fb\.watch|m\.facebook\.com/.test(url)) return "facebook";
  return null;
}

interface PostFeedProps {
  league?: any;
  onPosted?: () => void;
  feedToEdit?: IFeedToEdit;
  handleOnCancelEdit?: () => void;
}
export interface IFeedToEdit {
  id?: string;
  platform?:
    | "youtube"
    | "x"
    | "twitter"
    | "instagram"
    | "tiktok"
    | "facebook"
    | string;
  content?: string;
  likeCounter?: number;
  link?: string;
  createdBy?: string;
  createdAt?: string | Date;
  shareCounter?: number;
  authorName?: string;
  authorPic?: string;
  imageUrl?: string;
  videoUrl?: string;
  likedBy?: string[];
  [key: string]: any;
}

interface LinkPreview {
  url: string;
  title?: string;
  description?: string;
  image?: string;
  domain?: string;
}

interface MediaFile {
  file?: File | null;
  url: string;
  type: "image" | "video";
  remote?: boolean; // true when media comes from an existing feed (URL only)
}

export default function PostFeed({
  league,
  onPosted,
  feedToEdit,
  handleOnCancelEdit,
}: PostFeedProps) {
  const { user } = useAuth();
  const [postText, setPostText] = useState("");
  const [hostUser, setHostUser] = useState<any>(null);
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedArea, setCroppedArea] = useState<any>(null);
  const [loadingCropper, setLoadingCropper] = useState(false);
  const [aspect, setAspect] = useState<number>(16 / 9);
  const [linkPreview, setLinkPreview] = useState<LinkPreview | null>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-focus textarea on mount and when entering edit mode
  useEffect(() => {
    // small timeout ensures any modal/layout is ready
    const t = setTimeout(() => {
      try {
        const ta = textareaRef.current;
        if (ta) {
          ta.focus();
          // move cursor to end
          const len = ta.value?.length ?? 0;
          ta.setSelectionRange(len, len);
        }
      } catch (e) {
        // ignore
      }
    }, 50);
    return () => clearTimeout(t);
  }, [feedToEdit]);
  useEffect(() => {
    if (feedToEdit) {
      setHostUser({ imageUrl: feedToEdit?.authorPic || null });

      // Prefill post text
      setPostText(feedToEdit.content ?? "");

      // Prefill link preview if provided
      if (feedToEdit.link) {
        const platform = getPlatform(feedToEdit.link);
        if (platform) {
          setLinkPreview({ url: feedToEdit.link });
        } else {
          // non-social links: keep minimal preview object
          setLinkPreview({ url: feedToEdit.link });
        }
      }

      // Prefill media if imageUrl or videoUrl present
      const initialMedia: MediaFile[] = [];
      if (feedToEdit.imageUrl) {
        initialMedia.push({
          file: null,
          url: feedToEdit.imageUrl,
          type: "image",
          remote: true,
        });
      }
      if (feedToEdit.videoUrl) {
        initialMedia.push({
          file: null,
          url: feedToEdit.videoUrl,
          type: "video",
          remote: true,
        });
      }
      if (initialMedia.length > 0) setMediaFiles(initialMedia);
    } else {
      setHostUser(
        league?.teams?.find((team: any) => user?.uid === team?.createdBy)
      );
    }
  }, [user, league?.teams, feedToEdit]);

  // Extract and validate URLs from text
  const extractUrls = useCallback((text: string): string[] => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const urls = text.match(urlRegex) || [];

    // Filter out valid URLs only
    return urls.filter((url) => {
      try {
        new URL(url);
        return true;
      } catch {
        return false;
      }
    });
  }, []);

  // Debounced link preview generation
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(
    null
  );

  // Note: general link preview fetching has been disabled (CORS/perf reasons).
  // Social platform URLs are set directly to `linkPreview` and rendered by `SocialPlatformEmbed`.

  const debouncedGenerateLinkPreview = useCallback(
    (url: string) => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        setDebounceTimer(null);
      }
      const platform = getPlatform(url);
      if (platform) {
        // For social links, set preview directly for embed, skip fetch
        setLinkPreview({ url });
        setIsLoadingPreview(false);
        return;
      }
      // For general links, do not generate previews. Ensure no preview is shown.
      setLinkPreview(null);
      setIsLoadingPreview(false);
    },
    [debounceTimer]
  );

  // Handle text change and link detection
  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newText = e.target.value;
      setPostText(newText);

      const urls = extractUrls(newText);
      if (urls.length > 0 && (!linkPreview || urls[0] !== linkPreview.url)) {
        debouncedGenerateLinkPreview(urls[0]);
      } else if (urls.length === 0 && linkPreview) {
        setLinkPreview(null);
      }
    },
    [extractUrls, debouncedGenerateLinkPreview, linkPreview]
  );

  // Handle image upload
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    const imageFile = files.find((f) => f.type.startsWith("image/"));
    if (!imageFile) {
      event.target.value = "";
      return;
    }

    const maxImageBytes = 5 * 1024 * 1024; // 10MB
    if (imageFile.size > maxImageBytes) {
      setError("Image must be 5MB or smaller.");
      event.target.value = "";
      return;
    }

    // Open cropper with resized preview for better UX
    setError(null);
    setLoadingCropper(true);
    resizeImage(imageFile)
      .then(({ resizedUrl }) => {
        setTempImageUrl(resizedUrl);
        setShowCropper(true);
      })
      .catch(() => {
        // fallback to object URL
        setTempImageUrl(URL.createObjectURL(imageFile));
        setShowCropper(true);
      })
      .finally(() => {
        setLoadingCropper(false);
      });

    // Reset input
    event.target.value = "";
  };

  // Handle video upload
  const handleVideoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    // allow only one video; replace existing video if present
    const videoFile = files.find((f) => f.type.startsWith("video/"));
    if (!videoFile) {
      event.target.value = "";
      return;
    }

    const maxVideoBytes = 15 * 1024 * 1024; // 15MB
    if (videoFile.size > maxVideoBytes) {
      setError("Video must be 15MB or smaller.");
      event.target.value = "";
      return;
    }

    setError(null);
    const url = URL.createObjectURL(videoFile);
    setMediaFiles((prev) => {
      // remove previous video if exists
      const withoutVideos = prev.filter((m) => m.type !== "video");
      return [...withoutVideos, { file: videoFile, url, type: "video" }];
    });
    // Reset input
    event.target.value = "";
  };

  const onCropComplete = useCallback((_: any, croppedPixels: any) => {
    setCroppedArea(croppedPixels);
  }, []);

  const handleCropConfirm = useCallback(async () => {
    if (!tempImageUrl || !croppedArea) return;
    setLoadingCropper(true);
    try {
      const cropped = await cropImageFile(tempImageUrl, croppedArea);
      if (cropped) {
        // enforce size limit after cropping
        const maxImageBytes = 5 * 1024 * 1024;
        if (cropped.size > maxImageBytes) {
          setError("Cropped image must be 5MB or smaller.");
          return;
        }
        const url = URL.createObjectURL(cropped);
        setMediaFiles((prev) => {
          const withoutImages = prev.filter((m) => m.type !== "image");
          return [...withoutImages, { file: cropped, url, type: "image" }];
        });
      }
    } finally {
      setLoadingCropper(false);
      setShowCropper(false);
      setTempImageUrl(null);
      setCroppedArea(null);
      setCrop({ x: 0, y: 0 });
      setZoom(1);
    }
  }, [tempImageUrl, croppedArea]);

  // Remove media file
  const removeMediaFile = (index: number) => {
    setMediaFiles((prev) => {
      const updated = [...prev];
      // Only revoke object URLs we created locally
      if (!updated[index].remote) {
        try {
          URL.revokeObjectURL(updated[index].url);
        } catch (e) {
          // ignore
        }
      }
      updated.splice(index, 1);
      return updated;
    });
  };

  // Remove link preview
  const removeLinkPreview = () => {
    setLinkPreview(null);
  };

  // Check if post has content
  const hasContent = Boolean(
    postText.trim() || mediaFiles.length > 0 || linkPreview
  );

  // Submit handler
  const handleSubmit = async () => {
    setError(null);
    setIsPosting(true);

    // Require at least some text or a file/link
    if (!postText.trim() && mediaFiles.length === 0 && !linkPreview) {
      setError("Please add text, an image, a video, or a link before posting.");
      return;
    }

    // Enforce at most one image and one video
    const images = mediaFiles.filter((m) => m.type === "image");
    const videos = mediaFiles.filter((m) => m.type === "video");
    if (images.length > 1 || videos.length > 1) {
      setError("You can only upload one image and one video per post.");
      return;
    }

    // Final size checks (redundant but safe) - only check local files
    if (images[0] && images[0].file && images[0].file.size > 5 * 1024 * 1024) {
      setError("Image must be 5MB or smaller.");
      return;
    }
    if (videos[0] && videos[0].file && videos[0].file.size > 15 * 1024 * 1024) {
      setError("Video must be 15MB or smaller.");
      return;
    }

    try {
      // Build the payload object that will be stored in Firestore
      const feedPayload: any = {
        content: postText.trim(),
        createdBy: feedToEdit?.createdBy ?? user?.uid ?? "",
        // Preserve counters when editing an existing feed, otherwise initialize to 0
        likeCounter:
          typeof feedToEdit?.likeCounter === "number"
            ? feedToEdit.likeCounter
            : 0,
        shareCounter:
          typeof feedToEdit?.shareCounter === "number"
            ? feedToEdit.shareCounter
            : 0,
      };

      // Attach link preview info if present
      if (linkPreview?.url) {
        feedPayload.link = linkPreview.url;
        // keep some metadata if available (non-blocking)
        const platform = getPlatform(linkPreview.url);
        if (platform) feedPayload.platform = platform;
      }

      // Upload image / video to storage and add URLs to payload
      // Also detect whether the existing feed had a link/image/video so we
      // can explicitly clear fields when the user removes them during edit.
      const hadImageBefore = !!feedToEdit?.imageUrl;
      const hadVideoBefore = !!feedToEdit?.videoUrl;
      const hadLinkBefore = !!feedToEdit?.link;
      if (images[0]) {
        if (images[0].file) {
          const imageFile = images[0].file as File;
          const imagePath = `leagueLocker/${
            league ? league.id : feedToEdit?.leagueId
          }/feeds/${Date.now()}_${imageFile.name}`;
          const imageUrl = await uploadImageWithPath(imageFile, imagePath);
          if (imageUrl) feedPayload.imageUrl = imageUrl;
        } else if (images[0].remote) {
          feedPayload.imageUrl = images[0].url;
        }
      }

      // If editing and the feed previously had an image but the user removed it,
      // explicitly clear the field so merge doesn't keep the old URL.
      if (feedToEdit?.id && !images[0] && hadImageBefore) {
        feedPayload.imageUrl = null;
      }

      // If editing and the feed previously had a link but the user removed it,
      // explicitly clear both the link and platform so merge doesn't keep the
      // old values.
      if (feedToEdit?.id && !linkPreview?.url && hadLinkBefore) {
        feedPayload.link = null;
        feedPayload.platform = null;
      }

      // Handle video: upload local file or reuse remote URL
      if (videos[0]) {
        if (videos[0].file) {
          const videoFile = videos[0].file as File;
          const videoPath = `leagueLocker/${
            league ? league.id : feedToEdit?.leagueId
          }/feeds/${Date.now()}_${videoFile.name}`;
          const videoUrl = await uploadVideo(videoFile, videoPath);
          if (videoUrl) feedPayload.videoUrl = videoUrl;
        } else if (videos[0].remote) {
          feedPayload.videoUrl = videos[0].url;
        }
      }

      // If editing and the feed previously had a video but the user removed it,
      // explicitly clear the field so merge doesn't keep the old URL.
      if (feedToEdit?.id && !videos[0] && hadVideoBefore) {
        feedPayload.videoUrl = null;
      }

      // Persist to Firestore (addLeagueFeed handles add or edit when feedId provided)
      const res = await addLeagueFeed(
        league ? league.id : feedToEdit?.leagueId,
        feedPayload,
        feedToEdit?.id // if editing, pass feed id to update existing doc
      );
      // Only update progress step if a commissioner is creating a new feed
      if (league.createdBy === user?.uid) {
        await updateLeagueProgressStep(league.id, "make-first-post");
      }
      // Cleanup local state + object URLs for new posts only.ß
      // When editing an existing feed, avoid clearing previews immediately
      // because the parent may rehydrate the feed (causing a brief
      // disappearance/flicker). The parent should handle closing the edit
      // UI via `handleOnCancelEdit` which is called below.
      if (!feedToEdit?.id) {
        mediaFiles.forEach((m) => {
          if (!m.remote) {
            try {
              URL.revokeObjectURL(m.url);
            } catch (e) {
              // ignore
            }
          }
        });
        setMediaFiles([]);
        setPostText("");
        setLinkPreview(null);
      }

      setError(null);
      setIsPosting(false);
      // notify parent that a new post was added/updated so it can refetch
      try {
        onPosted && onPosted();
      } catch (e) {
        // swallow
      }

      // If editing, call cancel handler to close edit mode (if provided)
      try {
        if (feedToEdit?.id && handleOnCancelEdit) handleOnCancelEdit();
      } catch (e) {
        // swallow
      }
    } catch (err) {
      console.error("handleSubmit error:", err);
      setError("Failed to post. Please try again.");
      setIsPosting(false);
    }
  };

  return (
    <div className="feedBlk">
      {/* Media Preview Section */}
      {mediaFiles.length > 0 && (
        <div
          className="uploadedItemsWrap"
          style={{
            gridTemplateColumns: `repeat(${Math.min(
              mediaFiles.length,
              3
            )}, 1fr)`,
          }}
        >
          {mediaFiles.map((media, index) => (
            <div key={index} className="uploadedItemBlk">
              {media.type === "image" ? (
                <img src={media.url} alt={`Upload ${index + 1}`} />
              ) : (
                <video
                  src={media.url}
                  // className="w-full h-28 object-cover rounded-md"
                  controls
                />
              )}
              <button
                onClick={() => removeMediaFile(index)}
                className="closeIcon"
              >
                <X />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Header with profile picture and text input */}
      <div className="feedPostSection">
        {/* Profile Picture */}
        <div className="userPic">
          {hostUser?.imageUrl ? (
            <img src={hostUser?.imageUrl} alt="Profile" />
          ) : (
            <IconUser width={40} />
          )}
        </div>

        {/* Text Input Area */}
        <textarea
          ref={textareaRef}
          value={postText}
          onChange={handleTextChange}
          placeholder="write your post here"
          className="textareaStyle"
          style={{ fontFamily: "Roboto Condensed, sans-serif" }}
        />
      </div>

      {/* Link Preview Section (Rich Social Preview) - moved below textarea */}
      {(linkPreview || isLoadingPreview) && (
        <div className="mb-4">
          {isLoadingPreview ? (
            <div className="border border-gray-200 rounded-lg p-4 animate-pulse">
              <div className="flex space-x-3">
                <div className="w-16 h-16 bg-gray-200 rounded"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ) : (
            linkPreview &&
            (() => {
              const url = linkPreview.url;
              const platform = getPlatform(url);
              if (platform) {
                return (
                  <>
                    <button
                      onClick={removeLinkPreview}
                      className="absolute top-2 right-2 w-6 h-6 bg-gray-800 bg-opacity-70 hover:bg-opacity-90 text-white rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-10"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="w-full flex justify-center items-center bg-white">
                      <SocialPlatformEmbed
                        key={url}
                        url={url}
                        platform={platform}
                        width={"100%"}
                        height={platform === "youtube" ? 300 : "100%"}
                      />
                    </div>
                  </>
                );
              } else {
                return (
                  <>
                    <button
                      onClick={removeLinkPreview}
                      className="absolute top-2 right-2 w-6 h-6 bg-gray-800 bg-opacity-70 hover:bg-opacity-90 text-white rounded-full flex items-center justify-center transition-all opacity-0 group-hover:opacity-100 z-10"
                    >
                      <X className="w-3 h-3" />
                    </button>
                    <div className="flex">
                      {linkPreview.image && (
                        <img
                          src={linkPreview.image}
                          alt="Link preview"
                          className="w-14 h-14 object-cover"
                        />
                      )}
                      <div className="p-3 flex-1">
                        <div className="flex items-center space-x-1 text-gray-500 text-xs mb-1">
                          <ExternalLink className="w-3 h-3" />
                          <span>{linkPreview.domain}</span>
                        </div>
                        <h4 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2">
                          {linkPreview.title}
                        </h4>
                        <p className="text-xs text-gray-600 line-clamp-2">
                          {linkPreview.description}
                        </p>
                      </div>
                    </div>
                  </>
                );
              }
            })()
          )}
        </div>
      )}

      {/* Cropper modal */}
      {showCropper && tempImageUrl ? (
        <div className="popupWrap">
          <div className="popupContBlk">
            <div className="cropHeading">
              <strong>Aspect:</strong>
              <div className="cropOptions">
                <button
                  onClick={() => setAspect(16 / 9)}
                  className={`${aspect === 16 / 9 ? "active" : ""}`}
                >
                  16:9
                </button>
                <button
                  onClick={() => setAspect(9 / 16)}
                  className={`${aspect === 9 / 16 ? "active" : ""}`}
                >
                  9:16
                </button>
                <button
                  onClick={() => setAspect(1)}
                  className={`${aspect === 1 ? "active" : ""}`}
                >
                  1:1
                </button>
              </div>
            </div>
            <div style={{ position: "relative", width: "100%", height: 400 }}>
              <Cropper
                image={tempImageUrl}
                crop={crop}
                zoom={zoom}
                aspect={aspect}
                onCropChange={(c) => setCrop(c)}
                onZoomChange={(z) => setZoom(z)}
                onCropComplete={onCropComplete}
              />
            </div>
            <div className="btnWrap">
              <button
                onClick={() => {
                  setShowCropper(false);
                  setTempImageUrl(null);
                  setCroppedArea(null);
                  setCrop({ x: 0, y: 0 });
                  setZoom(1);
                }}
                className="btnStyle"
              >
                Cancel
              </button>
              <button
                onClick={handleCropConfirm}
                disabled={loadingCropper}
                className="btnFilled"
              >
                {loadingCropper ? "Processing..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="feedBlkBottom">
        {/* Upload Buttons */}
        <div className="uploadOptionWrap">
          {/* Upload Photo Button */}
          <label className="uploadOption">
            {/* <Camera className="w-5 h-5" /> */}
            <IconImg />
            <span>Upload Photo</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              className="hidden"
            />
          </label>

          {/* Upload Video Button */}
          <label className="uploadOption">
            <IconVideo />
            {/* <Play className="w-5 h-5 fill-current" /> */}
            <span>Upload Video</span>
            <input
              type="file"
              accept="video/*"
              multiple
              onChange={handleVideoUpload}
              className="hidden"
            />
          </label>
        </div>

        {/* Post Button */}
        {error && <div className="postError">{error}</div>}
        {feedToEdit?.id ? (
          <div className="btnWrap">
            <button
              onClick={() => {
                try {
                  handleOnCancelEdit && handleOnCancelEdit();
                } catch (e) {}
              }}
              className="btnStyle"
              disabled={isPosting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="btnFilled"
              disabled={!hasContent || isPosting}
            >
              Save
            </button>
          </div>
        ) : (
          <button
            onClick={handleSubmit}
            className="btnFilled"
            disabled={!hasContent || isPosting}
          >
            POST
          </button>
        )}
      </div>
    </div>
  );
}
